import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { fmtFR } from "@/lib/dates";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Building2, Trash2, Pencil, Percent, Search, ExternalLink, Copy, Link2, XCircle, RefreshCw, UserCircle2 } from "lucide-react";
import { formatIntermittentName } from "@/lib/intermittent";

const EMPTY = { name: "", kind: "company", intermittent_id: "", margin_rate: 20, color: "#134FE4", contact_last_name: "", contact_first_name: "", contact_role: "", contact_email: "", contact_phone: "", contacts: [], notes: "" };

const COLOR_SWATCHES = [
  "#134FE4", "#0B1B3A", "#E7FE55", "#D9704E", "#2FB47C",
  "#8E44AD", "#EFC94C", "#3AB0FF", "#F26A8D", "#4C6B22",
];

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [intermittents, setIntermittents] = useState([]);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const [portalOpen, setPortalOpen] = useState(false);
  const [portalClient, setPortalClient] = useState(null);
  const [portalData, setPortalData] = useState({ declarations: [], total_ht: 0, total_employer: 0, count: 0 });
  const [portalLink, setPortalLink] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);

  const load = async () => {
    try {
      const [{ data: cs }, { data: ints }] = await Promise.all([
        api.get("/clients"),
        api.get("/intermittents").catch(() => ({ data: [] })),
      ]);
      setClients(cs);
      setIntermittents(ints);
    }
    catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = clients.filter(c =>
    !q || c.name?.toLowerCase().includes(q.toLowerCase()) || c.contact_name?.toLowerCase().includes(q.toLowerCase())
  );

  const openNew = () => { setForm(EMPTY); setEditingId(null); setDialogOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditingId(c.id); setDialogOpen(true); };

  const openPortal = async (c) => {
    setPortalClient(c);
    setPortalOpen(true);
    try {
      const [{ data: agg }, { data: links }, { data: crs }] = await Promise.all([
        api.get(`/clients/${c.id}/declarations`),
        api.get("/public-links"),
        api.get("/change-requests", { params: { status: "pending" } }).catch(() => ({ data: [] })),
      ]);
      setPortalData(agg);
      setPortalLink(links.find(l => l.type === "client_portal" && l.client_id === c.id) || null);
      setChangeRequests((crs || []).filter(x => x.client_id === c.id));
    } catch (e) { toast.error(extractError(e)); }
  };

  const createPortalLink = async () => {
    try {
      const slug = window.prompt(
        "URL personnalisée du portail (optionnel) — laissez vide pour un token aléatoire.\n\nEx : seal-prod → …/portail-client/seal-prod",
        ""
      );
      if (slug === null) return;
      const { data } = await api.post("/public-links", {
        label: `Portail — ${portalClient.name}`,
        client_id: portalClient.id,
        active: true,
        type: "client_portal",
        slug: (slug || "").trim() || null,
      });
      setPortalLink(data);
      await navigator.clipboard.writeText(data.url).catch(() => {});
      toast.success("Lien portail créé et copié");
    } catch (e) { toast.error(extractError(e)); }
  };
  const editPortalSlug = async () => {
    if (!portalLink) return;
    const raw = window.prompt(
      "URL personnalisée du portail (a-z, 0-9, tirets — 3 à 60 caractères). Laissez vide pour un token aléatoire.",
      portalLink.slug || ""
    );
    if (raw === null) return;
    try {
      const { data: updated } = await api.patch(`/public-links/${portalLink.token}`, { slug: raw.trim() });
      // Recharge le lien mis à jour
      const { data: links } = await api.get("/public-links");
      const fresh = links.find(l => l.token === portalLink.token);
      if (fresh) setPortalLink(fresh);
      toast.success(raw.trim() ? "URL personnalisée mise à jour" : "URL personnalisée retirée");
    } catch (e) { toast.error(extractError(e)); }
  };
  const togglePortalLink = async () => {
    if (!portalLink) return;
    try {
      await api.patch(`/public-links/${portalLink.token}`, { active: !portalLink.active });
      setPortalLink({ ...portalLink, active: !portalLink.active });
    } catch (e) { toast.error(extractError(e)); }
  };
  const copyPortalLink = async () => {
    if (!portalLink) return;
    try { await navigator.clipboard.writeText(portalLink.url); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };
  const resolveChangeRequest = async (id, status) => {
    try {
      await api.patch(`/change-requests/${id}`, { status });
      toast.success(status === "handled" ? "Marquée comme traitée" : "Rejetée");
      const { data: crs } = await api.get("/change-requests", { params: { status: "pending" } });
      setChangeRequests((crs || []).filter(x => x.client_id === portalClient.id));
    } catch (e) { toast.error(extractError(e)); }
  };

  const save = async () => {
    try {
      const payload = { ...form, margin_rate: parseFloat(form.margin_rate) || 0 };
      if (!payload.name?.trim()) { toast.error("Nom de l'entreprise requis"); return; }
      if (payload.kind === "intermittent" && !payload.intermittent_id) {
        toast.error("Sélectionnez l'intermittent lié"); return;
      }
      if (editingId) await api.patch(`/clients/${editingId}`, payload);
      else await api.post("/clients", payload);
      toast.success(editingId ? "Client modifié" : "Client créé");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const remove = async (c) => {
    if (!confirm(`Supprimer ${c.name} ?`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const avgMargin = clients.length ? (clients.reduce((s, c) => s + (Number(c.margin_rate) || 0), 0) / clients.length).toFixed(1) : "—";

  return (
    <div className="space-y-6" data-testid="clients-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Clients</div>
          <h1 className="font-brand text-3xl md:text-4xl tracking-tight">Entreprises & taux de marge</h1>
          <p className="text-sm text-muted-foreground mt-1">Gérez vos entreprises clientes et leur taux de marge appliqué aux déclarations.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher..." className="pl-9 h-10 w-56 bg-muted" data-testid="clients-search" />
          </div>
          <Button className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-glow active:scale-95" onClick={openNew} data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-2" /> Nouveau client
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border/60 shadow-warm"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Clients enregistrés</div>
          <div className="font-display text-3xl font-semibold tabular-nums mt-1" data-testid="clients-count">{clients.length}</div>
        </CardContent></Card>
        <Card className="border-border/60 shadow-warm"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Taux de marge moyen</div>
          <div className="font-display text-3xl font-semibold tabular-nums mt-1 text-primary">{avgMargin} %</div>
        </CardContent></Card>
        <Card className="border-border/60 shadow-warm"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Info</div>
          <div className="text-sm mt-1">Le taux de marge s'applique automatiquement lors des <span className="font-semibold">demandes de déclaration</span> reçues via les liens publics.</div>
        </CardContent></Card>
      </div>

      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                  <th className="py-3 px-5 font-medium">Entreprise</th>
                  <th className="py-3 px-5 font-medium">Contact</th>
                  <th className="py-3 px-5 font-medium text-right">Taux marge</th>
                  <th className="py-3 px-5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="py-10 text-center text-muted-foreground">
                    <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />Aucun client
                  </td></tr>
                )}
                {filtered.map(c => (
                  <tr key={c.id} className="border-b border-border/40 hover:bg-muted fade-up" data-testid={`client-row-${c.id}`}>
                    <td className="py-3 px-5">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: (c.color || "#134FE4") + "1A", color: c.color || "#134FE4" }}
                          data-testid={`client-color-swatch-${c.id}`}
                        >
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="font-medium cursor-pointer" onClick={() => openEdit(c)}>{c.name}</div>
                          {c.notes && <div className="text-xs text-muted-foreground truncate max-w-[280px]">{c.notes}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-5 text-muted-foreground">
                      {(c.contact_first_name || c.contact_last_name) && (
                        <div className="text-sm text-foreground">{c.contact_first_name} {c.contact_last_name}{c.contact_role ? ` — ${c.contact_role}` : ""}</div>
                      )}
                      <div className="text-xs">{c.contact_email}</div>
                      <div className="text-xs">{c.contact_phone}</div>
                    </td>
                    <td className="py-3 px-5 text-right">
                      <Badge className="bg-secondary text-secondary-foreground border-secondary rounded-full font-semibold tabular-nums">
                        {Number(c.margin_rate || 0).toFixed(1)} %
                      </Badge>
                    </td>
                    <td className="py-3 px-5 text-right">
                      <Button variant="outline" size="sm" className="rounded-full mr-1" onClick={() => openPortal(c)} data-testid={`portal-btn-${c.id}`}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Déclarations & portail
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => remove(c)} data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Nom de l'entreprise ou de l'intermittent *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Zenith Auvergne / DUPONT Jean" data-testid="client-name" />
            </div>

            <div className="md:col-span-2">
              <Label>Type de client *</Label>
              <div className="flex gap-3 mt-1" data-testid="client-kind">
                <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${form.kind === "company" ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                  <input type="radio" name="kind" value="company" checked={form.kind === "company"} onChange={() => setForm({ ...form, kind: "company" })} data-testid="client-kind-company" />
                  <Building2 className="h-4 w-4" />
                  <span className="text-sm font-semibold">Entreprise</span>
                </label>
                <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${form.kind === "intermittent" ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                  <input type="radio" name="kind" value="intermittent" checked={form.kind === "intermittent"} onChange={() => setForm({ ...form, kind: "intermittent" })} data-testid="client-kind-intermittent" />
                  <UserCircle2 className="h-4 w-4" />
                  <span className="text-sm font-semibold">Intermittent</span>
                </label>
              </div>
            </div>

            {form.kind === "intermittent" && (
              <div className="md:col-span-2">
                <Label>Intermittent lié * <span className="text-[11px] text-muted-foreground font-normal">(les demandes de ce client seront auto-assignées à cet intermittent)</span></Label>
                <Select value={form.intermittent_id || ""} onValueChange={v => setForm({ ...form, intermittent_id: v })}>
                  <SelectTrigger data-testid="client-intermittent-select"><SelectValue placeholder="Sélectionner l'intermittent..." /></SelectTrigger>
                  <SelectContent>
                    {intermittents.map(p => (
                      <SelectItem key={p.id} value={p.id}>{formatIntermittentName(p)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="md:col-span-2">
              <Label className="flex items-center gap-1"><Percent className="h-3 w-3" /> Taux de marge (%)</Label>
              <Input type="number" min="0" max="100" step="0.1" value={form.margin_rate} onChange={e => setForm({ ...form, margin_rate: e.target.value })} data-testid="client-margin" />
              <div className="text-[11px] text-muted-foreground mt-1">
                Coût employeur = HT × (1 − taux). Ex : HT 600 € × 20 % = 480 € employeur (marge 120 €).
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Couleur (identifiant visuel du client)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={form.color || "#134FE4"}
                  onChange={e => setForm({ ...form, color: e.target.value })}
                  className="h-10 w-14 rounded-lg border border-border/60 cursor-pointer bg-transparent p-1"
                  data-testid="client-color-input"
                />
                <div className="flex flex-wrap gap-1.5" data-testid="client-color-swatches">
                  {COLOR_SWATCHES.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${form.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: c }}
                      aria-label={`Couleur ${c}`}
                    />
                  ))}
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Cette couleur teintera les cartes de déclarations issues d'une demande de ce client dans le kanban.
              </div>
            </div>
            <div className="md:col-span-2"><Label>Contact principal — Nom</Label><Input value={form.contact_last_name} onChange={e => setForm({ ...form, contact_last_name: e.target.value })} data-testid="client-contact-last" /></div>
            <div><Label>Contact principal — Prénom</Label><Input value={form.contact_first_name} onChange={e => setForm({ ...form, contact_first_name: e.target.value })} data-testid="client-contact-first" /></div>
            <div><Label>Poste</Label><Input value={form.contact_role} onChange={e => setForm({ ...form, contact_role: e.target.value })} placeholder="Gérant, Directeur..." data-testid="client-role" /></div>
            <div><Label>Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} data-testid="client-email" /></div>
            <div><Label>Téléphone</Label><Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} data-testid="client-phone" /></div>

            <div className="md:col-span-2 border-t border-border/40 pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-1"><UserCircle2 className="h-3 w-3" /> Contacts supplémentaires ({(form.contacts || []).length})</Label>
                <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setForm({ ...form, contacts: [...(form.contacts || []), { first_name: "", last_name: "", role: "", email: "", phone: "" }] })} data-testid="add-contact">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter un contact
                </Button>
              </div>
              {(form.contacts || []).map((ct, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2 p-3 bg-muted rounded-lg border border-border/40" data-testid={`contact-row-${idx}`}>
                  <Input placeholder="Nom" value={ct.last_name || ""} onChange={e => { const arr = [...form.contacts]; arr[idx] = { ...arr[idx], last_name: e.target.value }; setForm({ ...form, contacts: arr }); }} />
                  <Input placeholder="Prénom" value={ct.first_name || ""} onChange={e => { const arr = [...form.contacts]; arr[idx] = { ...arr[idx], first_name: e.target.value }; setForm({ ...form, contacts: arr }); }} />
                  <Input placeholder="Poste" value={ct.role || ""} onChange={e => { const arr = [...form.contacts]; arr[idx] = { ...arr[idx], role: e.target.value }; setForm({ ...form, contacts: arr }); }} />
                  <Input placeholder="Email" type="email" value={ct.email || ""} onChange={e => { const arr = [...form.contacts]; arr[idx] = { ...arr[idx], email: e.target.value }; setForm({ ...form, contacts: arr }); }} />
                  <div className="flex gap-1">
                    <Input placeholder="Téléphone" value={ct.phone || ""} onChange={e => { const arr = [...form.contacts]; arr[idx] = { ...arr[idx], phone: e.target.value }; setForm({ ...form, contacts: arr }); }} />
                    <Button type="button" size="icon" variant="ghost" className="text-destructive shrink-0" onClick={() => { const arr = form.contacts.filter((_, i) => i !== idx); setForm({ ...form, contacts: arr }); }} data-testid={`remove-contact-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {(!form.contacts || form.contacts.length === 0) && (
                <div className="text-[11px] text-muted-foreground italic">Aucun contact supplémentaire. Cliquez sur "Ajouter un contact" pour en créer un.</div>
              )}
            </div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="client-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button className="rounded-full active:scale-95 bg-primary" onClick={save} data-testid="confirm-client">{editingId ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portal / declarations dialog */}
      <Dialog open={portalOpen} onOpenChange={setPortalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="portal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" style={{ color: portalClient?.color || "#134FE4" }} />
              Déclarations & portail — {portalClient?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="border border-border/60 rounded-lg p-4 space-y-3 bg-muted">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1"><Link2 className="h-3 w-3" /> Lien du portail client</div>
            {portalLink ? (
              <>
                <div className="text-[11px] font-mono text-muted-foreground truncate" data-testid="portal-link-url">{portalLink.url}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={copyPortalLink} data-testid="portal-copy"><Copy className="h-3.5 w-3.5 mr-1" /> Copier</Button>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={editPortalSlug} data-testid="portal-edit-slug"><Pencil className="h-3.5 w-3.5 mr-1" /> URL perso</Button>
                  <a href={portalLink.url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Ouvrir</Button></a>
                  <Button variant={portalLink.active ? "ghost" : "outline"} size="sm" className="rounded-full" onClick={togglePortalLink} data-testid="portal-toggle">
                    {portalLink.active ? "Désactiver" : "Activer"}
                  </Button>
                  <Badge variant="outline" className="rounded-full text-[10px]">{portalLink.used_count || 0} vue(s)</Badge>
                  {!portalLink.active && <Badge variant="outline" className="rounded-full text-[10px] text-destructive border-destructive/40">Inactif</Badge>}
                </div>
              </>
            ) : (
              <Button className="rounded-full bg-primary active:scale-95" onClick={createPortalLink} data-testid="portal-create"><Plus className="h-4 w-4 mr-2" /> Créer & copier le lien portail</Button>
            )}
            <div className="text-[11px] text-muted-foreground">
              Ce lien permet au client de voir ses déclarations par mois, demander une nouvelle déclaration, et solliciter une modification ou annulation.
            </div>
          </div>

          {changeRequests.length > 0 && (
            <div className="border border-accent/40 bg-accent/20 rounded-lg p-4 space-y-2">
              <div className="text-xs uppercase tracking-widest font-semibold flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Demandes en attente ({changeRequests.length})</div>
              {changeRequests.map(cr => (
                <div key={cr.id} className="flex items-center gap-3 text-sm border-t border-border/40 pt-2" data-testid={`change-request-${cr.id}`}>
                  <Badge className="rounded-full text-[10px]" variant={cr.action === "cancel" ? "destructive" : "outline"}>
                    {cr.action === "cancel" ? "Annulation" : "Modification"}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-muted-foreground truncate">
                      {cr.declaration?.event_name || "—"} · {cr.declaration?.start_date || "—"} · {cr.intermittent ? formatIntermittentName(cr.intermittent) : ""}
                    </div>
                    {cr.message && <div className="text-xs italic">"{cr.message}"</div>}
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => resolveChangeRequest(cr.id, "handled")} data-testid={`resolve-${cr.id}`}>Traitée</Button>
                  <Button variant="ghost" size="sm" className="rounded-full text-destructive" onClick={() => resolveChangeRequest(cr.id, "rejected")} data-testid={`reject-${cr.id}`}>Rejeter</Button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="border border-border/60 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Déclarations</div>
              <div className="font-display text-2xl font-semibold tabular-nums mt-1">{portalData.count}</div>
            </div>
            <div className="border border-border/60 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Total HT</div>
              <div className="font-display text-2xl font-semibold tabular-nums mt-1" style={{ color: portalClient?.color || "#134FE4" }}>{portalData.total_ht.toLocaleString("fr-FR")} €</div>
            </div>
            <div className="border border-border/60 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Coût employeur cumulé</div>
              <div className="font-display text-2xl font-semibold tabular-nums mt-1">{portalData.total_employer.toLocaleString("fr-FR")} €</div>
            </div>
          </div>

          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Déclarations liées</div>
          <div className="border border-border/60 rounded-lg overflow-hidden">
            {portalData.declarations.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Aucune déclaration pour ce client.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 px-3 font-medium">Période</th>
                    <th className="py-2 px-3 font-medium">Intermittent</th>
                    <th className="py-2 px-3 font-medium">Événement</th>
                    <th className="py-2 px-3 font-medium">Dates</th>
                    <th className="py-2 px-3 font-medium">Statut</th>
                    <th className="py-2 px-3 font-medium text-right">HT</th>
                  </tr>
                </thead>
                <tbody>
                  {portalData.declarations.map(d => (
                    <tr key={d.id} className="border-t border-border/40" data-testid={`portal-decl-row-${d.id}`}>
                      <td className="py-2 px-3 text-xs">{d.period || "—"}</td>
                      <td className="py-2 px-3">{d.intermittent ? formatIntermittentName(d.intermittent) : "—"}</td>
                      <td className="py-2 px-3 text-xs">{d.event_name || "—"}</td>
                      <td className="py-2 px-3 text-xs">{d.start_date ? fmtFR(d.start_date) : "—"}{d.end_date && d.end_date !== d.start_date ? ` → ${fmtFR(d.end_date)}` : ""}</td>
                      <td className="py-2 px-3"><Badge variant="outline" className="rounded-full text-[10px]">{d.status}</Badge>{d.pending_change && <Badge className="rounded-full text-[10px] ml-1 bg-accent text-accent-foreground border-accent">en attente</Badge>}</td>
                      <td className="py-2 px-3 text-right font-semibold tabular-nums">{Number(d.cost_ht_client || 0).toLocaleString("fr-FR")} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPortalOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
